import {
  memo,
  useState,
  Fragment,
  type FC,
} from 'react'
import {
  useNavigate
} from 'react-router-dom';
import {
  PaymentMethodKeyEnum
} from '@shared/types';
import {
  PaymentMethod,
  CheckoutResult,
  usePaymentsSelector,
  PaymentMethodSelector,
  PaymentUnavailableMessage,
  PaymentMethodOrderSetTypeEnum,
  selectRecentlyUsedPaymentMethod,
  selectWithdrawSinglePaymentMethod,
  useGetPaymentMethodOrderSetsQuery,
  selectDepositPaymentMethodOrderSets
} from '@entities/payments';
import {
  PurchaseTwoPaneLayout
} from '@widgets/purchase-two-pane-layout';
import {
  ConfirmCoinRedemptionWorldPayFrom
} from './confirm-coin-redemption-worldpay-form.component';
import {
  ConfirmCoinRedemptionPaymentIQForm
} from './confirm-coin-redemption-paymentiq-form.component';
import {
  ConfirmCoinRedemptionAptPayForm
} from './confirm-coin-redemption-aptpay-from.component';

export type CoinRedemptionCheckoutFormProps = {
  redeemAmount: number;
  onClose?(): void;
  onStart?(amount: string | number): void;
  onFailed?(result: {
    redeemAmount: Maybe<number>;
    paymentMethod: Maybe<PaymentMethod>;
  }): void;
  onSuccess?(result: {
    redeemAmount: Maybe<number>;
    paymentMethod: Maybe<PaymentMethod>;
  }): void;
};

export const CoinRedemptionCheckoutForm: FC<CoinRedemptionCheckoutFormProps> = memo(({
  redeemAmount,
  onStart,
  onClose,
  onFailed,
  onSuccess
}) => {
  const navigate = useNavigate();

  const { paymentMethods, recently, single } = usePaymentsSelector(state => ({
    paymentMethods: selectDepositPaymentMethodOrderSets(PaymentMethodOrderSetTypeEnum.Withdrawal)(state),
    recently: selectRecentlyUsedPaymentMethod(state),
    single: selectWithdrawSinglePaymentMethod(state),
  }));

  const [view, setView] = useState<'selector' | 'checkout'>(single ? 'checkout' : 'selector');
  const [paymentMethod, setPaymentMethod] = useState<Maybe<PaymentMethod>>(single || null);

  const { isFetching, refetch } = useGetPaymentMethodOrderSetsQuery(undefined, { refetchOnMountOrArgChange: true });

  const handlePaymentMethodSelection = (method: PaymentMethod): void => {
    setPaymentMethod(method);
    setView('checkout');
  };

  const handlePaymentSuccess = (result?: CheckoutResult): void => {
    if (paymentMethod?.key !== PaymentMethodKeyEnum.PaymentIQ) {
      onSuccess?.({ redeemAmount, paymentMethod });
      const state = {
        purchaseAmount: redeemAmount,
        paymentId: result?.paymentId,
        accountNumber: result?.cardNumber,
      };
      navigate('/redeem-success', { state });
    };
  };

  const handlePaymentFailed = (result?: CheckoutResult): void => {
    onFailed?.({ redeemAmount, paymentMethod });
    const state = {
      paymentMethod,
      purchaseAmount: redeemAmount,
      paymentId: result?.paymentId,
      accountNumber: result?.cardNumber,
    };
    navigate('/redeem-failed', { state });
  };

  const handlePaymentUnavailable = async (): Promise<void> => {
    setView('selector');
    setPaymentMethod(null);
    refetch();
  };

  return !isFetching && (
    <Fragment>
      {view === 'selector' && (
        <PurchaseTwoPaneLayout>
          <PurchaseTwoPaneLayout.Content
            title='Payment Methods'
            onBack={onClose}>
            <PaymentMethodSelector
              recently={recently}
              recommended={paymentMethods?.recommended}
              alternative={paymentMethods?.alternative}
              onSelect={handlePaymentMethodSelection}
            />
          </PurchaseTwoPaneLayout.Content>
        </PurchaseTwoPaneLayout>
      )}
      {view === 'checkout' && (
        ({
          [PaymentMethodKeyEnum.WorldPayCNPCard]: (
            <ConfirmCoinRedemptionWorldPayFrom
              redeemAmount={redeemAmount}
              onClose={onClose}
              onStart={onStart}
              onFail={handlePaymentFailed}
              onSuccess={handlePaymentSuccess}
            />
          ),
          [PaymentMethodKeyEnum.PaymentIQ]: (
            <ConfirmCoinRedemptionPaymentIQForm
              redeemAmount={redeemAmount}
              onClose={onClose}
              onStart={onStart}
              onFail={handlePaymentFailed}
              onSuccess={handlePaymentSuccess}
            />
          ),
          [PaymentMethodKeyEnum.AptPay]: (
            <ConfirmCoinRedemptionAptPayForm
              redeemAmount={redeemAmount}
              onClose={onClose}
              onStart={onStart}
              onFail={handlePaymentFailed}
              onSuccess={handlePaymentSuccess}
            />
          ),
          [PaymentMethodKeyEnum.Unipaas]: (
            <PurchaseTwoPaneLayout>
              <PurchaseTwoPaneLayout.Content>
                <PaymentUnavailableMessage
                  paymentMethodName='Unipaas'
                  onBack={handlePaymentUnavailable}
                />
              </PurchaseTwoPaneLayout.Content>
            </PurchaseTwoPaneLayout>
          ),
        })[paymentMethod?.key || PaymentMethodKeyEnum.AptPay]
      )}
    </Fragment>
  );
});